import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMechanicAuth } from '../../context/MechanicAuthContext';
import { updateMechanicProfile, changeMechanicPassword } from '../../api/mechanicAuth.api';
import '../../styles/mechanic.css';

const MechanicProfile = () => {
  const { mechanic, loginMechanic, token } = useMechanicAuth();

  const [form, setForm] = useState({ phone: mechanic?.phone || '', vehicleNumber: mechanic?.vehicleNumber || '', specialization: mechanic?.specialization || '' });
  const [passForm, setPassForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [changingPass, setChangingPass] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [passMsg, setPassMsg] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passError, setPassError] = useState('');

  const initials = (mechanic?.name || 'M').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError('');
    setSaving(true);
    try {
      const updated = await updateMechanicProfile(form);
      loginMechanic(token, { ...mechanic, ...updated });
      setProfileMsg('Profile updated successfully.');
      setTimeout(() => setProfileMsg(''), 3000);
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const handlePassChange = async (e) => {
    e.preventDefault();
    if (passForm.newPassword.length < 6) { setPassError('New password must be at least 6 characters.'); return; }
    if (passForm.newPassword !== passForm.confirmPassword) { setPassError('Passwords do not match.'); return; }
    setPassError('');
    setChangingPass(true);
    try {
      await changeMechanicPassword({ currentPassword: passForm.currentPassword, newPassword: passForm.newPassword });
      setPassMsg('Password changed successfully.');
      setPassForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPassMsg(''), 3000);
    } catch (err) {
      setPassError(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setChangingPass(false);
    }
  };

  return (
    <div className="mechanic-page">
      <div className="mechanic-container">
        <div className="mechanic-header">
          <h1 className="mechanic-title">My Profile</h1>
          <Link to="/mechanic/dashboard" className="mechanic-back-btn">← Dashboard</Link>
        </div>

        {/* Avatar card */}
        <div className="mechanic-profile-card" style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
          <div className="mechanic-avatar">{initials}</div>
          <div>
            <div className="mechanic-profile-name">{mechanic?.name}</div>
            <div className="mechanic-profile-sub">{mechanic?.email}</div>
            <div className="mechanic-profile-sub">{mechanic?.phone}</div>
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem' }}>
              <span style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: '6px', padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}>
                ⭐ {parseFloat(mechanic?.rating || 5).toFixed(1)}
              </span>
              <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: '6px', padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}>
                {mechanic?.totalJobs || 0} jobs
              </span>
            </div>
          </div>
        </div>

        {/* Edit profile */}
        <div className="mechanic-profile-card">
          <div className="mechanic-section-title">Edit Profile</div>
          {profileMsg && <div className="auth-success-box" style={{ marginBottom: '1rem' }}>{profileMsg}</div>}
          {profileError && <div className="auth-error-box" style={{ marginBottom: '1rem' }}>{profileError}</div>}
          <form onSubmit={handleProfileSave} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="mechanic-field">
              <label className="mechanic-label">Full Name</label>
              <input className="mechanic-input" value={mechanic?.name || ''} disabled />
            </div>
            <div className="mechanic-field">
              <label className="mechanic-label">Email</label>
              <input className="mechanic-input" value={mechanic?.email || ''} disabled />
            </div>
            <div className="mechanic-field">
              <label className="mechanic-label">Phone Number</label>
              <input className="mechanic-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" />
            </div>
            <div className="mechanic-field">
              <label className="mechanic-label">Vehicle Number</label>
              <input className="mechanic-input" value={form.vehicleNumber} onChange={(e) => setForm({ ...form, vehicleNumber: e.target.value })} placeholder="RJ14 AB 1234" />
            </div>
            <div className="mechanic-field">
              <label className="mechanic-label">Specialization</label>
              <input className="mechanic-input" value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} placeholder="e.g. Tyre repair, Battery" />
            </div>
            <button type="submit" className="mechanic-save-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>

        {/* Change password */}
        <div className="mechanic-profile-card">
          <div className="mechanic-section-title">Change Password</div>
          {passMsg && <div className="auth-success-box" style={{ marginBottom: '1rem' }}>{passMsg}</div>}
          {passError && <div className="auth-error-box" style={{ marginBottom: '1rem' }}>{passError}</div>}
          <form onSubmit={handlePassChange} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="mechanic-field">
              <label className="mechanic-label">Current Password</label>
              <input type="password" className="mechanic-input" value={passForm.currentPassword} onChange={(e) => setPassForm({ ...passForm, currentPassword: e.target.value })} required />
            </div>
            <div className="mechanic-field">
              <label className="mechanic-label">New Password</label>
              <input type="password" className="mechanic-input" value={passForm.newPassword} onChange={(e) => setPassForm({ ...passForm, newPassword: e.target.value })} required />
            </div>
            <div className="mechanic-field">
              <label className="mechanic-label">Confirm New Password</label>
              <input type="password" className="mechanic-input" value={passForm.confirmPassword} onChange={(e) => setPassForm({ ...passForm, confirmPassword: e.target.value })} required />
            </div>
            <button type="submit" className="mechanic-save-btn" disabled={changingPass}>
              {changingPass ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MechanicProfile;
